import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
// FiBell moved to MainForm sidebar
import { io } from 'socket.io-client';
import { getApiUrl } from './utils/apiUrl';
import MainForm from './components/MainForm';
import AuthForm from './components/AuthForm';
import WidgetModal from './components/WidgetModal';
import DemoModeBanner from './components/DemoModeBanner';
import SplashScreen from './components/SplashScreen';
import { fetchConfig, updateConfig } from './services/configService';
import { fetchMyTeams } from './services/teamService';
import { fetchUnreadCountForUser } from './services/notificationService';
import { fetchUserPermissions } from './services/userPermissionsService';
import { getAssignedInquiriesCount } from './services/inquiryService';
import './App.css';

// Lazy-loaded widgets (code splitting — each widget loads on demand)
const ConfigurationForm = lazy(() => import('./components/ConfigurationForm'));
const WorkOrderWidget = lazy(() => import('./components/WorkOrderWidget'));
const ArbeidsordreListWidget = lazy(() => import('./components/ArbeidsordreListWidget'));
const OrdersFlowWidget = lazy(() => import('./components/OrdersFlowWidget'));
const MachineWidget = lazy(() => import('./components/MachineWidget'));
const TeamsWidget = lazy(() => import('./components/TeamsWidget'));
const SteelCertificateWidget = lazy(() => import('./components/SteelCertificateWidget'));
const EmployeeWidget = lazy(() => import('./components/EmployeeWidget'));
const RoleWidget = lazy(() => import('./components/RoleWidget'));
const EmployeeCalendarWidget = lazy(() => import('./components/EmployeeCalendarWidget'));
const AnstattkalendarWidget = lazy(() => import('./components/AnstattkalendarWidget'));
const ProductionPlanWidget = lazy(() => import('./components/ProductionPlanWidget'));
const JobQueueWidget = lazy(() => import('./components/JobQueueWidget'));
const VarslingerWidget = lazy(() => import('./components/VarslingerWidget'));
const StatisticsWidget = lazy(() => import('./components/StatisticsWidget'));
const StorageLocationWidget = lazy(() => import('./components/StorageLocationWidget'));
const AuditLoggerWidget = lazy(() => import('./components/AuditLoggerWidget'));
const SystemBackupWidget = lazy(() => import('./components/SystemBackupWidget'));
const TeamMemberManagementWidget = lazy(() => import('./components/TeamMemberManagementWidget'));
const AdminPanelWidget = lazy(() => import('./components/AdminPanelWidget'));
const UnimicrWidget = lazy(() => import('./components/UnimicrWidget'));
const UnimicrLookupWidget = lazy(() => import('./components/UnimicrLookupWidget'));
const UnimicroOrdersWidget = lazy(() => import('./components/UnimicroOrdersWidget'));
const PartsExplorerWidget = lazy(() => import('./components/PartsExplorerWidget'));
const TimelineWidget = lazy(() => import('./components/TimelineWidget'));
const KPIDashboardWidget = lazy(() => import('./components/KPIDashboardWidget'));
const SalesProgressWidget = lazy(() => import('./components/SalesProgressWidget'));
const FeedWidget = lazy(() => import('./components/FeedWidget'));
const AIChatWidget = lazy(() => import('./components/AIChatWidget'));
const ForesporslerWidget = lazy(() => import('./components/ForesporslerWidget'));
const DeliveryDisplayWidget = lazy(() => import('./components/DeliveryDisplayWidget'));
const TeamCapacityWidget = lazy(() => import('./components/TeamCapacityWidget'));
const BOMWidget = lazy(() => import('./components/BOMWidget'));
const SteelInventoryWidget = lazy(() => import('./components/SteelInventoryWidget'));
const WeatherWidget = lazy(() => import('./components/WeatherWidget'));
const IncomingDeliveryWidget = lazy(() => import('./components/IncomingDeliveryWidget'));
const EmailWidget = lazy(() => import('./components/EmailWidget'));
const AlleAnsatteWidget = lazy(() => import('./components/AlleAnsatteWidget'));
const CadViewerWidget = lazy(() => import('./components/CadViewerWidget'));
const AdamCadWidget = lazy(() => import('./components/AdamCadWidget'));
const BrukereWidget = lazy(() => import('./components/BrukereWidget'));
const PhoneDirectoryWidget = lazy(() => import('./components/PhoneDirectoryWidget'));
const AbsenceRequestWidget = lazy(() => import('./components/AbsenceRequestWidget'));
const MachineMonitorWidget = lazy(() => import('./components/MachineMonitorWidget'));
const AlarmLogWidget = lazy(() => import('./components/AlarmLogWidget'));
const CncProgramTransferWidget = lazy(() => import('./components/CncProgramTransferWidget'));
const SmartSchedulerWidget = lazy(() => import('./components/SmartSchedulerWidget'));
const OperatorWorkWidget = lazy(() => import('./components/OperatorWorkWidget'));
const VektKalkulatorWidget = lazy(() => import('./components/VektKalkulatorWidget'));
const QuickLinksWidget = lazy(() => import('./components/QuickLinksWidget'));
const PdfRedactWidget = lazy(() => import('./components/PdfRedactWidget'));
const WidgetManagerWidget = lazy(() => import('./components/WidgetManagerWidget'));
const ForbruksskapWidget = lazy(() => import('./components/ForbruksskapWidget'));
const FileShareWidget = lazy(() => import('./components/FileShareWidget'));
const DeliveryTrackingWidget = lazy(() => import('./components/DeliveryTrackingWidget'));
const DailyReportWidget = lazy(() => import('./components/DailyReportWidget'));
const SteelPurchaseWidget = lazy(() => import('./components/SteelPurchaseWidget'));
const MachiningToolsWidget = lazy(() => import('./components/MachiningToolsWidget'));
const SteelMeasureWidget = lazy(() => import('./components/SteelMeasureWidget'));
const PartIssuesWidget = lazy(() => import('./components/PartIssuesWidget'));

const App = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    token: null,
  });
  const [initializing, setInitializing] = useState(true);

  const [dashboardSettings, setDashboardSettings] = useState({
    companyName: 'Modulpanel',
    layout: 'comfortable',
    refreshInterval: 15,
    notificationsEnabled: true,
    internalDeadlineDays: 14,
    deliveryDeadlineDays: 21,
    logoUrl: null,
    dashboardBgUrl: null,
    headerBgUrl: null,
    rightPanelBgUrl: null,
    kpiCardBgColor: null,
    widgetButtonBgColor: null,
    headerTextColor: null,
    kpiCardTextColor: null,
    widgetMenuTextColor: null,
    rightPanelTextColor: null,
    greetingTextColor: null,
    kpiCardVisibility: {},
  });

  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState('');
  const [userTeams, setUserTeams] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [assignedInquiriesCount, setAssignedInquiriesCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [forcePasswordNew, setForcePasswordNew] = useState('');
  const [forcePasswordConfirm, setForcePasswordConfirm] = useState('');
  const [forcePasswordError, setForcePasswordError] = useState('');
  const [forcePasswordLoading, setForcePasswordLoading] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');
  const [socketRef, setSocketRef] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [dmUser, setDmUser] = useState(null); // For direct message to specific user
  const [chatBanners, setChatBanners] = useState([]); // Array of { id, senderName, message, timestamp, isDM }
  const [fileShareBanners, setFileShareBanners] = useState([]); // Array of { id, senderName, fileName, timestamp }
  const [inquiryBanner, setInquiryBanner] = useState(null); // { assignedBy, companyName, inquiryId }
  const initialServerVersionRef = useRef(null); // Version when page loaded
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const onlineUsersTimeoutRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const chatBannerTimeoutRef = useRef(null);
  const [userPermissions, setUserPermissions] = useState({
    canCreateWorkOrder: false,
    canCreateCertificate: false,
    canPlanProduction: false,
    isAdmin: false,
    isTeamLeader: false,
    roleName: null,
    employeeId: null,
    employeeName: null,
    teamLeaderTeamNames: [],
  });

  // Enrich user object with permissions from API - must be at top level
  const enrichedUser = useMemo(() => {
    if (!authState.user) return null;
    return {
      ...authState.user,
      canCreateWorkOrder: userPermissions.canCreateWorkOrder,
      can_create_work_order: userPermissions.canCreateWorkOrder ? 1 : 0, // For helper functions
      canCreateCertificate: userPermissions.canCreateCertificate,
      canPlanProduction: userPermissions.canPlanProduction,
      canPlanAllTeams: userPermissions.canPlanAllTeams,
      canManageEmployees: userPermissions.canManageEmployees,
      canReserveDates: userPermissions.canReserveDates,
      roleName: userPermissions.roleName,
      employeeId: userPermissions.employeeId,
      isAdmin: userPermissions.isAdmin,
      isTeamLeader: userPermissions.isTeamLeader, // CRITICAL: Team leader flag
      teamLeaderTeamNames: userPermissions.teamLeaderTeamNames || [],
      isVacationApprover: userPermissions.isVacationApprover,
    };
  }, [authState.user, userPermissions]);

  const loadConfig = async (token) => {
    try {
      setLoadingConfig(true);
      setConfigError('');
      const config = await fetchConfig(token);
      setDashboardSettings({
        companyName: config.companyName,
        layout: config.layout,
        refreshInterval: config.refreshInterval,
        notificationsEnabled: config.notificationsEnabled,
        internalDeadlineDays: config.internalDeadlineDays || 14,
        deliveryDeadlineDays: config.deliveryDeadlineDays || 21,
        unimicrEnabled: config.unimicrEnabled || false,
        hourlyPackingCost: config.hourlyPackingCost || 850,
        euPallPrice: config.euPallPrice || 200,
        halfEuPallPrice: config.halfEuPallPrice || 150,
        euKarmPrice: config.euKarmPrice || 100,
        halfEuKarmPrice: config.halfEuKarmPrice || 75,
        euLokkPrice: config.euLokkPrice || 120,
        halfEuLokkPrice: config.halfEuLokkPrice || 90,
        machineDisplayIds: config.machineDisplayIds || [],
        displaySettings: config.displaySettings || { delivery: true, machine: true, fellesKalender: true },
        logoUrl: config.logoUrl || null,
        dashboardBgUrl: config.dashboardBgUrl || null,
        headerBgUrl: config.headerBgUrl || null,
        rightPanelBgUrl: config.rightPanelBgUrl || null,
        kpiCardBgColor: config.kpiCardBgColor || null,
        widgetButtonBgColor: config.widgetButtonBgColor || null,
        headerTextColor: config.headerTextColor || null,
        kpiCardTextColor: config.kpiCardTextColor || null,
        widgetMenuTextColor: config.widgetMenuTextColor || null,
        rightPanelTextColor: config.rightPanelTextColor || null,
      greetingTextColor: config.greetingTextColor || null,
        kpiCardVisibility: config.kpiCardVisibility || {},
        playStoreUrl: config.playStoreUrl || '',
        appStoreUrl: config.appStoreUrl || '',
        mobileServerUrl: config.mobileServerUrl || '',
      });
    } catch (error) {
      console.error(error);
      setConfigError(error.message || 'Kunne ikke laste konfigurasjon');
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadUserTeams = async (token) => {
    try {
      const teams = await fetchMyTeams(token);
      setUserTeams(teams);
    } catch (err) {
      console.error('Could not load user teams:', err);
      setUserTeams([]);
    }
  };

  const loadUnreadCount = async (token) => {
    try {
      const data = await fetchUnreadCountForUser(token);
      setUnreadNotificationsCount(data.count || 0);
    } catch (err) {
      console.error('Could not load unread count:', err);
    }
  };

  const loadUserPermissions = async (token) => {
    try {
      const permissions = await fetchUserPermissions(token);
      setUserPermissions(permissions);
    } catch (err) {
      console.error('Could not load user permissions:', err);
      // Default to no permissions on error
      setUserPermissions({
        canCreateWorkOrder: false,
        canCreateCertificate: false,
        canPlanProduction: false,
        isAdmin: false,
        isTeamLeader: false,
        employeeId: null,
        employeeName: null,
        teamLeaderTeamNames: [],
      });
    }
  };

  const loadAssignedInquiriesCount = async (token) => {
    try {
      const data = await getAssignedInquiriesCount(token);
      setAssignedInquiriesCount(data.count || 0);
    } catch (err) {
      console.error('Could not load assigned inquiries count:', err);
      setAssignedInquiriesCount(0);
    }
  };

  const handleAuthSuccess = ({ token, user, forcePasswordChange: mustChange }) => {
    localStorage.setItem('authToken', token);
    setAuthState({ isAuthenticated: true, user, token });
    setInitializing(false);
    if (mustChange) {
      setForcePasswordChange(true);
    } else {
      loadConfig(token);
      loadUserTeams(token);
      loadUnreadCount(token);
      loadUserPermissions(token);
      loadAssignedInquiriesCount(token);
    }
  };

  const handleForcePasswordSubmit = async (e) => {
    e.preventDefault();
    setForcePasswordError('');
    if (forcePasswordNew.length < 8) {
      setForcePasswordError('Passord m\u00e5 v\u00e6re minst 8 tegn');
      return;
    }
    if (forcePasswordNew !== forcePasswordConfirm) {
      setForcePasswordError('Passordene stemmer ikke overens');
      return;
    }
    setForcePasswordLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authState.token}` },
        body: JSON.stringify({ newPassword: forcePasswordNew }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      const activeToken = data.token || authState.token;
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        setAuthState((prev) => ({ ...prev, token: data.token }));
      }
      setForcePasswordChange(false);
      setForcePasswordNew('');
      setForcePasswordConfirm('');
      loadConfig(activeToken);
      loadUserTeams(activeToken);
      loadUnreadCount(activeToken);
      loadUserPermissions(activeToken);
      loadAssignedInquiriesCount(activeToken);
    } catch (err) {
      setForcePasswordError(err.message || 'Kunne ikke endre passord');
    } finally {
      setForcePasswordLoading(false);
    }
  };

  const handleSignOut = (message = '') => {
    localStorage.removeItem('authToken');
    setAuthState({ isAuthenticated: false, user: null, token: null });
    setInitializing(false);
    setUserTeams([]);
    setUnreadNotificationsCount(0);
    setForcePasswordChange(false);
    if (message) setSessionExpiredMsg(message);
  };

  const [selectedWidget, setSelectedWidget] = useState(null);
  const [previousWidget, setPreviousWidget] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [productionPlanInitialDate, setProductionPlanInitialDate] = useState(null);
  const [bomInquiryId, setBomInquiryId] = useState(null);
  const [preFilledEmployeeData, setPreFilledEmployeeData] = useState(null);
  const [cadViewerFile, setCadViewerFile] = useState(null);
  const [pendingInquiryId, setPendingInquiryId] = useState(null);

  // Open Forespørsler widget when URL has #inquiry=ID (from email link)
  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash;
      const inquiryMatch = hash.match(/inquiry=(\d+)/);
      if (inquiryMatch) {
        const id = inquiryMatch[1];
        setPendingInquiryId(id);
        setSelectedWidget('foresporsler');
        // Clear the hash so it doesn't re-trigger on refresh
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        return;
      }
      // Open Ordreflytt when URL has #ordre=ID (from status change email link)
      const orderMatch = hash.match(/ordre=(\d+)/);
      if (orderMatch) {
        const id = orderMatch[1];
        sessionStorage.setItem('selectedWorkOrderId', id);
        setSelectedWidget('ordersFlow');
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };
    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  // Hide splash screen after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Activity heartbeat — reports 1 minute of usage every 60s while the
  // browser tab is visible (background/idle tabs are not counted)
  useEffect(() => {
    if (!authState.token) return undefined;
    const beat = () => {
      if (document.visibilityState === 'visible') {
        fetch(`${getApiUrl()}/activity/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authState.token}` },
        }).catch(() => {});
      }
    };
    beat();
    const interval = setInterval(beat, 60000);
    document.addEventListener('visibilitychange', beat);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [authState.token]);

  // Auto-open Daily Report (Good Morning modal) once per day
  // Access is determined by the API (team leaders, verkstedsforman, fakturering members, admin)
  useEffect(() => {
    if (!enrichedUser || !authState.token) return;

    const today = new Date().toISOString().slice(0, 10);
    const key = `dailyReportRead_${today}`;
    if (localStorage.getItem(key)) return;

    // Fetch report to check allowed + auto-open settings
    const checkAutoOpen = async () => {
      try {
        const { fetchDailyReport } = await import('./services/dailyReportService');
        const report = await fetchDailyReport(authState.token);
        if (!report.allowed) return;
        if (!report.config?.autoOpenEnabled) return;

        const hour = new Date().getHours();
        const autoOpenHour = report.config.autoOpenHour ?? 6;
        if (hour < autoOpenHour) return;

        // Wait for splash screen to finish
        setTimeout(() => {
          setSelectedWidget('dailyReport');
          // Note: don't set localStorage here — only set when user clicks "I have read"
        }, 5500);
      } catch (err) {
        // Silently fail — don't block app loading
        console.warn('[DailyReport] Auto-open check failed:', err.message);
      }
    };

    checkAutoOpen();
  }, [enrichedUser, authState.token]);

  // Set initializing to false when auth is properly initialized
  useEffect(() => {
    if (authInitialized && authState.user) {
      setInitializing(false);
    }
  }, [authInitialized, authState.user]);

  // Decode JWT token to get user info
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        const decoded = decodeToken(token);
        if (decoded) {
          // Check if token is expired
          const now = Math.floor(Date.now() / 1000);
          if (decoded.exp && decoded.exp < now) {
            localStorage.removeItem('authToken');
            setInitializing(false);
            setAuthInitialized(true);
            return;
          }
          
          const user = {
            id: decoded.sub,
            email: decoded.email,
            firstName: decoded.firstName,
            lastName: decoded.lastName,
            isAdmin: decoded.isAdmin || false,
          };
          setAuthState({ isAuthenticated: true, user, token });
          
          // Load all data
          await Promise.all([
            loadConfig(token),
            loadUserTeams(token),
            loadUnreadCount(token),
            loadUserPermissions(token),
            loadAssignedInquiriesCount(token)
          ]);
          
          setAuthInitialized(true);
        } else {
          // Invalid token, remove it
          localStorage.removeItem('authToken');
          setInitializing(false);
          setAuthInitialized(true);
        }
      } else {
        // No token found
        setInitializing(false);
        setAuthInitialized(true);
      }
    };
    
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global 401 interceptor — logs user out when token becomes invalid
  // (e.g. admin changed their password, deactivation, or token expired)
  useEffect(() => {
    if (!authState.isAuthenticated) return;
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401) {
        console.log('[AUTH] Token invalid (401) — logging out');
        handleSignOut('Din sesjon har utløpt. Vennligst logg inn på nytt.');
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated]);

  // Poll for unread notifications every 30 seconds
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) return;

    loadUnreadCount(authState.token);
    const interval = setInterval(() => {
      loadUnreadCount(authState.token);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, authState.token]);

  // Refresh user permissions when tab regains focus (handles team leader rotation while logged in)
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUserPermissions(authState.token);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authState.isAuthenticated, authState.token]);

  // Socket.IO connection for online users tracking
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.user) return;

    const apiUrl = getApiUrl().replace('/api', '');
    const socket = io(apiUrl, {
      transports: ['websocket', 'polling'],
      auth: { token: authState.token }
    });

    socket.on('connect', () => {
      // Identity is verified server-side from the JWT — no need to emit userOnline
    });

    socket.on('onlineUsersUpdated', (users) => {
      setOnlineUsers(users || []);
    });

    // Listen for new chat messages to show notification
    socket.on('newChatMessage', (msg) => {
      // Only count if message is from someone else and chat is not open
      if (msg.senderId !== authState.user?.id) {
        setUnreadChatCount(prev => prev + 1);
        
        // Add banner notification to stack
        const newBanner = {
          id: `${msg.senderId}_${Date.now()}`,
          senderName: msg.senderName || 'Ukjent',
          message: msg.message,
          timestamp: Date.now(),
          isDM: msg.isDM || false
        };
        setChatBanners(prev => [...prev, newBanner]);
      }
    });

    // Listen for config updates from admin changes
    socket.on('configUpdated', (newConfig) => {
      setDashboardSettings((prev) => ({ ...prev, ...newConfig }));
    });

    // Listen for file share notifications
    socket.on('fileShareReceived', (data) => {
      if (data.recipientId === authState.user?.id) {
        const newBanner = {
          id: `fileshare_${Date.now()}_${Math.random()}`,
          senderId: data.senderId,
          fileName: data.fileName,
          timestamp: Date.now(),
        };
        setFileShareBanners(prev => [...prev, newBanner]);
        // Auto-dismiss after 8 seconds
        setTimeout(() => {
          setFileShareBanners(prev => prev.filter(b => b.id !== newBanner.id));
        }, 8000);
      }
    });

    // Listen for server version to detect new deployments
    socket.on('serverVersion', (version) => {
      if (!initialServerVersionRef.current) {
        initialServerVersionRef.current = version;
      } else if (initialServerVersionRef.current !== version) {
        setShowUpdateToast(true);
      }
    });

    // Listen for inquiry assignments
    socket.on('inquiryAssigned', (data) => {
      console.log('[INQUIRY] Received assignment:', data);
      setInquiryBanner({
        assignedBy: data.assignedBy || 'Ukjent',
        companyName: data.companyName || 'Ukjent firma',
        inquiryId: data.inquiryId,
        timestamp: Date.now()
      });
      // Increment assigned inquiries count
      setAssignedInquiriesCount(prev => prev + 1);
    });

    // Store socket reference for chat
    setSocketRef(socket);

    return () => {
      socket.disconnect();
      setSocketRef(null);
    };
  }, [authState.isAuthenticated, authState.user]);

  // Listen for openWidget custom events from other components
  useEffect(() => {
    const handleOpenWidget = (event) => {
      const detail = event.detail;
      
      // Handle both old format (string) and new format (object with widgetName and initialDate)
      if (typeof detail === 'string') {
        // Old format: just widget name
        setPreviousWidget(selectedWidget);
        setSelectedWidget(detail);
      } else if (detail && detail.widgetName) {
        // New format: object with widgetName and optional initialDate
        setPreviousWidget(selectedWidget);
        setSelectedWidget(detail.widgetName);
        
        // If opening production plan with a specific date, save it
        if (detail.widgetName === 'productionPlan' && detail.initialDate) {
          setProductionPlanInitialDate(detail.initialDate);
        }
      }
    };

    window.addEventListener('openWidget', handleOpenWidget);
    return () => window.removeEventListener('openWidget', handleOpenWidget);
  }, [selectedWidget]);

  const handleWidgetSelect = (widgetId) => {
    setSelectedWidget(widgetId);
    // Reset production plan initial date when switching widgets
    if (widgetId !== 'productionPlan') {
      setProductionPlanInitialDate(null);
    }
  };

  const handleCloseModal = () => {
    // Refresh unread count when closing varslinger
    if (selectedWidget === 'varslinger' && authState.token) {
      loadUnreadCount(authState.token);
    }
    
    // Reset production plan initial date
    setProductionPlanInitialDate(null);
    
    // Reset BOM inquiry ID
    setBomInquiryId(null);
    
    // Reset pre-filled employee data
    setPreFilledEmployeeData(null);

    // Reset CAD viewer file
    setCadViewerFile(null);
    
    // If there's a previous widget, go back to it instead of closing to main menu
    if (previousWidget) {
      setSelectedWidget(previousWidget);
      setPreviousWidget(null);
    } else {
      setSelectedWidget(null);
      setPreviousWidget(null);
    }
  };

  const handleCreateEmployee = (user) => {
    // Set pre-filled data from user
    setPreFilledEmployeeData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userId: user.id,
    });
    // Open admin panel (which contains EmployeeWidget)
    setSelectedWidget('adminPanel');
  };

  const renderWidgetModalContent = () => {
    if (!selectedWidget) return null;

    const token = authState.token;

    return (
      <Suspense fallback={<div className="tw-flex tw-items-center tw-justify-center tw-p-8" />}>
        {(() => {
          switch (selectedWidget) {
      case 'configuration':
        return (
          <ConfigurationForm
            initialSettings={dashboardSettings}
            token={token}
            onLogoUploaded={(logoUrl) => {
              setDashboardSettings((prev) => ({ ...prev, logoUrl }));
            }}
            onBgUploaded={(dashboardBgUrl) => {
              setDashboardSettings((prev) => ({ ...prev, dashboardBgUrl }));
            }}
            onHeaderBgUploaded={(headerBgUrl) => {
              setDashboardSettings((prev) => ({ ...prev, headerBgUrl }));
            }}
            onRightPanelBgUploaded={(rightPanelBgUrl) => {
              setDashboardSettings((prev) => ({ ...prev, rightPanelBgUrl }));
            }}
            onSubmit={async (settings) => {
              if (!token) {
                throw new Error('Manglende innloggingstoken');
              }
              const saved = await updateConfig(settings, token);
              setDashboardSettings((prev) => ({
                ...prev,
                companyName: saved.companyName,
                layout: saved.layout,
                refreshInterval: saved.refreshInterval,
                notificationsEnabled: saved.notificationsEnabled,
                internalDeadlineDays: saved.internalDeadlineDays || 14,
                deliveryDeadlineDays: saved.deliveryDeadlineDays || 21,
                unimicrEnabled: saved.unimicrEnabled || false,
                hourlyPackingCost: saved.hourlyPackingCost || 850,
                euPallPrice: saved.euPallPrice || 200,
                halfEuPallPrice: saved.halfEuPallPrice || 150,
                euKarmPrice: saved.euKarmPrice || 100,
                halfEuKarmPrice: saved.halfEuKarmPrice || 75,
                euLokkPrice: saved.euLokkPrice || 120,
                halfEuLokkPrice: saved.halfEuLokkPrice || 90,
                machineDisplayIds: saved.machineDisplayIds || [],
                displaySettings: saved.displaySettings || prev.displaySettings || { delivery: true, machine: true, fellesKalender: true },
                logoUrl: saved.logoUrl || prev.logoUrl || null,
                dashboardBgUrl: saved.dashboardBgUrl || prev.dashboardBgUrl || null,
                headerBgUrl: saved.headerBgUrl || prev.headerBgUrl || null,
                rightPanelBgUrl: saved.rightPanelBgUrl || prev.rightPanelBgUrl || null,
                kpiCardBgColor: saved.kpiCardBgColor || null,
                widgetButtonBgColor: saved.widgetButtonBgColor || null,
                headerTextColor: saved.headerTextColor || null,
                kpiCardTextColor: saved.kpiCardTextColor || null,
                widgetMenuTextColor: saved.widgetMenuTextColor || null,
                rightPanelTextColor: saved.rightPanelTextColor || null,
                greetingTextColor: saved.greetingTextColor || null,
                kpiCardVisibility: saved.kpiCardVisibility || {},
                playStoreUrl: saved.playStoreUrl || '',
                appStoreUrl: saved.appStoreUrl || '',
                mobileServerUrl: saved.mobileServerUrl || '',
              }));
            }}
          />
        );
      case 'workOrders':
        return <WorkOrderWidget 
          token={token} 
          currentUser={enrichedUser}
          onOrderCreated={(createdOrder) => {
            console.log('[App] Order created:', createdOrder.id);
            // Close workOrders widget and open ordersFlow
            setSelectedWidget('ordersFlow');
          }}
          onClose={handleCloseModal}
        />;
      case 'workOrdersList':
        return <ArbeidsordreListWidget 
          token={token} 
          currentUser={enrichedUser} 
          userTeams={userTeams}
          onClose={previousWidget ? handleCloseModal : undefined}
          onOpenCadViewer={(file) => {
            setCadViewerFile(file);
            setPreviousWidget('workOrdersList');
            setSelectedWidget('cadViewer');
          }}
        />;
      case 'ordersFlow':
        return <OrdersFlowWidget 
          token={token}
          currentUser={enrichedUser}
          userTeams={userTeams}
          config={dashboardSettings}
          onOpenWorkOrderDetails={(orderId) => {
            console.log('[App] Opening work order details for ID:', orderId);
            // Store the order ID to highlight/open it
            sessionStorage.setItem('selectedWorkOrderId', orderId);
            console.log('[App] Stored in sessionStorage:', sessionStorage.getItem('selectedWorkOrderId'));
            // Save current widget as previous before switching
            setPreviousWidget('ordersFlow');
            // Open workOrdersList widget with the selected order
            setSelectedWidget('workOrdersList');
            console.log('[App] Changed widget to: workOrdersList');
          }}
          onOpenProductionPlan={() => {
            console.log('[App] Opening production plan');
            setPreviousWidget('ordersFlow');
            setSelectedWidget('productionPlan');
          }}
          onOpenCadViewer={(file) => {
            setCadViewerFile(file);
            setPreviousWidget('ordersFlow');
            setSelectedWidget('cadViewer');
          }}
          onCreateWorkOrder={() => {
            setPreviousWidget('ordersFlow');
            setSelectedWidget('workOrders');
          }}
          onOpenAvvik={(workOrderId) => {
            if (workOrderId) {
              sessionStorage.setItem('avvikFilterWorkOrderId', String(workOrderId));
            } else {
              sessionStorage.removeItem('avvikFilterWorkOrderId');
            }
            setPreviousWidget('ordersFlow');
            setSelectedWidget('avvik');
          }}
        />;
      case 'machines':
        return <MachineWidget token={token} />;
      case 'machineMonitor':
        return <MachineMonitorWidget token={token} onClose={handleCloseModal} onOpenMachines={() => {
          setPreviousWidget('machineMonitor');
          setSelectedWidget('machines');
        }} />;
      case 'alarmLog':
        return <AlarmLogWidget token={token} isAdmin={enrichedUser?.isAdmin} />;
      case 'teams':
        return <TeamsWidget token={token} />;
      case 'teamMembers':
        return null; // rendered fullscreen below
      case 'steelCertificates':
        return <SteelCertificateWidget token={token} currentUser={enrichedUser} />;
      case 'steelInventory':
        return <SteelInventoryWidget token={token} currentUser={enrichedUser} userTeams={userTeams} />;
      case 'steelPurchases':
        return <SteelPurchaseWidget token={token} currentUser={enrichedUser} />;
      case 'machiningTools':
        return <MachiningToolsWidget token={token} currentUser={enrichedUser} />;
      case 'steelMeasure':
        return <SteelMeasureWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'roles':
        return <RoleWidget token={token} />;
      case 'productionPlan':
        return <ProductionPlanWidget token={token} currentUser={enrichedUser} initialDate={productionPlanInitialDate} onClose={handleCloseModal} />;
      case 'jobQueue':
        return <JobQueueWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(orderId) => {
          sessionStorage.setItem('selectedWorkOrderId', orderId);
          setPreviousWidget('jobQueue');
          setSelectedWidget('ordersFlow');
        }} />;
      case 'employeeCalendar':
        return <EmployeeCalendarWidget token={token} currentUser={enrichedUser} />;
      case 'anstattkalendar':
        return <AnstattkalendarWidget token={token} currentUser={enrichedUser} />;
      case 'email':
        return <EmailWidget token={token} currentUser={enrichedUser} />;
      case 'foresporsler':
        return <ForesporslerWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'varslinger':
        // Refresh unread count when closing varslinger widget
        return <VarslingerWidget token={token} onClose={() => { handleCloseModal(); loadUnreadCount(token); }} onOpenInOrdersFlow={(workOrderId) => {
          sessionStorage.setItem('selectedWorkOrderId', workOrderId);
          setPreviousWidget('varslinger');
          setSelectedWidget('ordersFlow');
        }} />;
      case 'statistics':
        return <StatisticsWidget token={token} />;
      case 'storageLocations':
        return <StorageLocationWidget token={token} currentUser={enrichedUser} />;
      case 'auditLog':
        return <AuditLoggerWidget token={token} onClose={handleCloseModal} />;
      case 'systemBackup':
        return <SystemBackupWidget token={token} currentUser={enrichedUser} />;
      case 'unimicro':
        return <UnimicrWidget token={token} currentUser={enrichedUser} />;
      case 'unimicrLookup':
        return <UnimicrLookupWidget token={token} />;
      case 'partsExplorer':
        return <PartsExplorerWidget 
          token={token} 
          onClose={handleCloseModal}
          initialSearchTerm={sessionStorage.getItem('partsExplorerSearch')}
          onOpenWorkOrderDetails={(orderId) => {
            console.log('[App] Opening work order details from PartsExplorer for ID:', orderId);
            sessionStorage.setItem('selectedWorkOrderId', orderId);
            setPreviousWidget('partsExplorer');
            setSelectedWidget('workOrdersList');
          }}
          onOpenInOrdersFlow={(orderId) => {
            console.log('[App] Opening OrdersFlow from PartsExplorer for work order ID:', orderId);
            sessionStorage.setItem('selectedWorkOrderId', orderId);
            setPreviousWidget('partsExplorer');
            setSelectedWidget('ordersFlow');
          }}
        />;
      case 'timeline':
        return <TimelineWidget 
          token={token} 
          currentUser={enrichedUser}
          onClose={handleCloseModal}
          onOpenWorkOrderDetails={(orderId) => {
            console.log('[App] Opening work order details from Timeline for ID:', orderId);
            sessionStorage.setItem('selectedWorkOrderId', orderId);
            setPreviousWidget('timeline');
            setSelectedWidget('workOrdersList');
          }}
        />;
      case 'kpiDashboard':
        return <KPIDashboardWidget 
          token={token} 
          currentUser={enrichedUser}
          onClose={handleCloseModal}
          kpiCardBgColor={dashboardSettings.kpiCardBgColor}
          kpiCardVisibility={dashboardSettings.kpiCardVisibility}
        />;
      case 'salesProgress':
        return <SalesProgressWidget
          token={token}
          currentUser={enrichedUser}
          userTeams={userTeams}
          onClose={handleCloseModal}
        />;
      case 'deliveryDisplay':
        return <DeliveryDisplayWidget token={token} currentUser={enrichedUser} onOpenWorkOrder={(orderId) => {
          sessionStorage.setItem('selectedWorkOrderId', orderId);
          setPreviousWidget('deliveryDisplay');
          setSelectedWidget('workOrdersList');
        }} onOpenInOrdersFlow={(orderId) => {
          sessionStorage.setItem('selectedWorkOrderId', orderId);
          setPreviousWidget('deliveryDisplay');
          setSelectedWidget('ordersFlow');
        }} />;
      case 'dailyReport':
        return <DailyReportWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(orderId) => {
          sessionStorage.setItem('selectedWorkOrderId', orderId);
          setPreviousWidget('dailyReport');
          setSelectedWidget('ordersFlow');
        }} />;
      case 'teamCapacity':
        return <TeamCapacityWidget token={token} currentUser={enrichedUser} />;
      case 'smartScheduler':
        return <SmartSchedulerWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'bom':
        return <BOMWidget token={token} currentUser={enrichedUser} initialInquiryId={bomInquiryId} onClose={handleCloseModal} onOpenInquiry={() => {
                setPreviousWidget('bom');
                setSelectedWidget('foresporsler');
              }} />;
      case 'weather':
        return <WeatherWidget />;
      case 'incomingDelivery':
        return <IncomingDeliveryWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'alleAnsatte':
        return <AlleAnsatteWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(workOrderId) => {
            sessionStorage.setItem('selectedWorkOrderId', workOrderId);
            setPreviousWidget('alleAnsatte');
            setSelectedWidget('ordersFlow');
          }} />;
      case 'brukere':
        return <BrukereWidget token={token} currentUser={enrichedUser} />;
      case 'phoneDirectory':
        return <PhoneDirectoryWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'absenceRequest':
        return <AbsenceRequestWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'operatorWork':
        return null; // rendered fullscreen below
      case 'cadViewer':
        return <CadViewerWidget token={token} onClose={handleCloseModal} initialFile={cadViewerFile} />;
      case 'vektKalkulator':
        return <VektKalkulatorWidget onClose={handleCloseModal} />;
      case 'quickLinks':
        return <QuickLinksWidget token={token} currentUser={enrichedUser} userPermissions={userPermissions} />;
      case 'pdfRedact':
        return <PdfRedactWidget />;
      case 'widgetManager':
        return <WidgetManagerWidget token={token} />;
      case 'forbruksskap':
        return <ForbruksskapWidget token={token} currentUser={enrichedUser} onClose={handleCloseModal} />;
      case 'fileShare':
        return <FileShareWidget token={token} currentUser={enrichedUser} socketRef={socketRef} onClose={handleCloseModal} />;
      default:
        return (
          <div className="modal-placeholder">
            <p>Configuration for <strong>{selectedWidget}</strong> will appear here.</p>
          </div>
        );
    }
        })()}
      </Suspense>
    );
  };

  if (initializing) {
    return (
      <div className="auth-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ color: '#94a3b8' }}>Laster...</p>
        </div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return (
      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <AuthForm onAuthSuccess={handleAuthSuccess} sessionExpiredMsg={sessionExpiredMsg} clearSessionExpiredMsg={() => setSessionExpiredMsg('')} />
      </div>
    );
  }

  if (forcePasswordChange) {
    return (
      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div style={{ background: '#111a2e', borderRadius: '14px', padding: '2rem', maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid rgba(148,163,184,0.14)' }}>
          <h2 style={{ marginBottom: '0.5rem', color: '#e5e9f0' }}>Bytt passord</h2>
          <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Administrator krever at du bytter passord f&oslash;r du kan fortsette.
          </p>
          <form onSubmit={handleForcePasswordSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.85rem', color: '#94a3b8' }}>Nytt passord</label>
              <input
                type="password"
                value={forcePasswordNew}
                onChange={(e) => setForcePasswordNew(e.target.value)}
                style={{ width: '100%', padding: '0.6rem', borderRadius: '10px', border: '1px solid rgba(148,163,184,0.22)', fontSize: '1rem', background: '#0b1220', color: '#e5e9f0' }}
                placeholder="Minst 8 tegn"
                autoFocus
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.85rem', color: '#94a3b8' }}>Bekreft passord</label>
              <input
                type="password"
                value={forcePasswordConfirm}
                onChange={(e) => setForcePasswordConfirm(e.target.value)}
                style={{ width: '100%', padding: '0.6rem', borderRadius: '10px', border: '1px solid rgba(148,163,184,0.22)', fontSize: '1rem', background: '#0b1220', color: '#e5e9f0' }}
                placeholder="Gjenta passord"
              />
            </div>
            {forcePasswordError && <p style={{ color: '#fca5a5', fontSize: '0.85rem', marginBottom: '1rem' }}>{forcePasswordError}</p>}
            <button
              type="submit"
              disabled={forcePasswordLoading}
              style={{ width: '100%', padding: '0.7rem', background: 'linear-gradient(135deg, #38bdf8, #3b82f6)', color: '#0b1220', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 8px 25px rgba(56,189,248,0.25)' }}
            >
              {forcePasswordLoading ? 'Lagrer...' : 'Sett nytt passord'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const headerTitle = dashboardSettings.companyName?.trim() || 'Modulpanel';

  const user = authState.user;
  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'Innlogget bruker';
  const userEmail = user?.email;

  return (
    <>
      {showSplash && <SplashScreen companyName={headerTitle} />}
      <div className="app">
        <DemoModeBanner />

      <main className="app-content">
        <MainForm
          user={user}
          token={authState.token}
          onWidgetSelect={handleWidgetSelect}
          onCloseWidget={handleCloseModal}
          selectedWidget={selectedWidget}
          companyName={headerTitle}
          logoUrl={dashboardSettings.logoUrl}
          dashboardBgUrl={dashboardSettings.dashboardBgUrl}
          headerBgUrl={dashboardSettings.headerBgUrl}
          rightPanelBgUrl={dashboardSettings.rightPanelBgUrl}
          kpiCardBgColor={dashboardSettings.kpiCardBgColor}
          kpiCardVisibility={dashboardSettings.kpiCardVisibility}
          widgetButtonBgColor={dashboardSettings.widgetButtonBgColor}
          headerTextColor={dashboardSettings.headerTextColor}
          kpiCardTextColor={dashboardSettings.kpiCardTextColor}
          widgetMenuTextColor={dashboardSettings.widgetMenuTextColor}
          rightPanelTextColor={dashboardSettings.rightPanelTextColor}
          greetingTextColor={dashboardSettings.greetingTextColor}
          unreadNotificationsCount={unreadNotificationsCount}
          assignedInquiriesCount={assignedInquiriesCount}
          userPermissions={userPermissions}
          userTeams={userTeams}
          onSignOut={handleSignOut}
          onlineUsers={onlineUsers}
          showOnlineUsers={showOnlineUsers}
          setShowOnlineUsers={setShowOnlineUsers}
          showChat={showChat}
          setShowChat={setShowChat}
          unreadChatCount={unreadChatCount}
          setUnreadChatCount={setUnreadChatCount}
          dmUser={dmUser}
          setDmUser={setDmUser}
          socketRef={socketRef}
          onlineUsersTimeoutRef={onlineUsersTimeoutRef}
        />
        <Suspense fallback={<div className="tw-flex tw-items-center tw-justify-center tw-h-full" />}>
        {/* OrdersFlow full screen (full viewport, covers sidebar like Felles Kalender) */}
        {selectedWidget === 'ordersFlow' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.95) 0%, rgba(44, 82, 130, 0.9) 50%, rgba(26, 54, 93, 0.95) 100%), repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255, 255, 255, 0.02) 50px, rgba(255, 255, 255, 0.02) 51px)'
          }}>
            {/* Full screen content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <OrdersFlowWidget 
                token={authState.token}
                currentUser={enrichedUser}
                userTeams={userTeams}
                config={dashboardSettings}
                onClose={handleCloseModal}
                onOpenWorkOrderDetails={(orderId) => {
                  console.log('[App] Opening work order details for ID:', orderId);
                  sessionStorage.setItem('selectedWorkOrderId', orderId);
                  console.log('[App] Stored in sessionStorage:', sessionStorage.getItem('selectedWorkOrderId'));
                  // Save current widget as previous before switching
                  setPreviousWidget('ordersFlow');
                  setSelectedWidget('workOrdersList');
                  console.log('[App] Changed widget to: workOrdersList');
                }}
                onOpenProductionPlan={() => {
                  console.log('[App] Opening production plan from fullscreen ordersFlow');
                  setPreviousWidget('ordersFlow');
                  setSelectedWidget('productionPlan');
                }}
                onOpenCadViewer={(file) => {
                  setCadViewerFile(file);
                  setPreviousWidget('ordersFlow');
                  setSelectedWidget('cadViewer');
                }}
                onCreateWorkOrder={() => {
                  setPreviousWidget('ordersFlow');
                  setSelectedWidget('workOrders');
                }}
                onOpenAvvik={(workOrderId) => {
                  if (workOrderId) {
                    sessionStorage.setItem('avvikFilterWorkOrderId', String(workOrderId));
                  } else {
                    sessionStorage.removeItem('avvikFilterWorkOrderId');
                  }
                  setPreviousWidget('ordersFlow');
                  setSelectedWidget('avvik');
                }}
              />
            </div>
          </div>
        )}
        {/* AlleAnsatte full screen */}
        {selectedWidget === 'alleAnsatte' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{ background: '#0f172a' }}>
            <AlleAnsatteWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(workOrderId) => {
              sessionStorage.setItem('selectedWorkOrderId', workOrderId);
              setPreviousWidget('alleAnsatte');
              setSelectedWidget('ordersFlow');
            }} />
          </div>
        )}
        {/* AdminPanel full screen */}
        {selectedWidget === 'adminPanel' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <AdminPanelWidget token={authState.token} currentUser={enrichedUser} onCreateEmployee={handleCreateEmployee} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* ProductionPlan full screen */}
        {selectedWidget === 'productionPlan' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.95) 0%, rgba(44, 82, 130, 0.9) 50%, rgba(26, 54, 93, 0.95) 100%), repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255, 255, 255, 0.02) 50px, rgba(255, 255, 255, 0.02) 51px)'
          }}>
            {/* Full screen content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <ProductionPlanWidget token={authState.token} currentUser={enrichedUser} initialDate={productionPlanInitialDate} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* JobQueue full screen */}
        {selectedWidget === 'jobQueue' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <JobQueueWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(orderId) => {
                sessionStorage.setItem('selectedWorkOrderId', orderId);
                setPreviousWidget('jobQueue');
                setSelectedWidget('ordersFlow');
              }} />
            </div>
          </div>
        )}
        {/* KPI Dashboard full screen */}
        {selectedWidget === 'kpiDashboard' && (
          <div className="widget-panel-fullscreen" style={{ background: '#0f172a' }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <KPIDashboardWidget 
                token={authState.token} 
                currentUser={enrichedUser} 
                onClose={handleCloseModal}
                kpiCardBgColor={dashboardSettings.kpiCardBgColor}
                kpiCardVisibility={dashboardSettings.kpiCardVisibility}
              />
            </div>
          </div>
        )}
        {/* Sales Progress full screen */}
        {selectedWidget === 'salesProgress' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <SalesProgressWidget
                token={authState.token}
                currentUser={enrichedUser}
                userTeams={userTeams}
                onClose={handleCloseModal}
              />
            </div>
          </div>
        )}
        {/* Arbeidsordre List full screen */}
        {selectedWidget === 'workOrdersList' && authState.isAuthenticated && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: '#f8fafc',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <ArbeidsordreListWidget
                token={authState.token}
                currentUser={enrichedUser}
                userTeams={userTeams}
                onClose={handleCloseModal}
                onOpenCadViewer={(file) => {
                  setCadViewerFile(file);
                  setPreviousWidget('workOrdersList');
                  setSelectedWidget('cadViewer');
                }}
              />
            </div>
          </div>,
          document.body
        )}
        {/* Delivery Display full screen */}
        {selectedWidget === 'deliveryDisplay' && (
          <div className="widget-panel-fullscreen">
            <div style={{ flex: 1, overflow: 'auto' }}>
              <DeliveryDisplayWidget onClose={handleCloseModal} token={authState.token} currentUser={enrichedUser} onOpenWorkOrder={(orderId) => {
                sessionStorage.setItem('selectedWorkOrderId', orderId);
                setPreviousWidget('deliveryDisplay');
                setSelectedWidget('workOrdersList');
              }} onOpenInOrdersFlow={(orderId) => {
                sessionStorage.setItem('selectedWorkOrderId', orderId);
                setPreviousWidget('deliveryDisplay');
                setSelectedWidget('ordersFlow');
              }} />
            </div>
          </div>
        )}
        {/* Delivery Tracking full screen */}
        {selectedWidget === 'deliveryTracking' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <DeliveryTrackingWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(orderId) => {
              sessionStorage.setItem('selectedWorkOrderId', orderId);
              setPreviousWidget('deliveryTracking');
              setSelectedWidget('ordersFlow');
            }} />
          </div>
        )}
        {/* Unimicro Ordrer full screen */}
        {selectedWidget === 'unimicroOrders' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <UnimicroOrdersWidget token={authState.token} onClose={handleCloseModal} />
          </div>
        )}
        {/* Team Medlemmer full screen */}
        {selectedWidget === 'teamMembers' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <TeamMemberManagementWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
          </div>
        )}
        {/* Min Arbeidsoversikt full screen */}
        {selectedWidget === 'operatorWork' && (
          <OperatorWorkWidget token={authState.token} onClose={handleCloseModal} />
        )}
        {/* Daily Report toast */}
        {selectedWidget === 'dailyReport' && (
          <DailyReportWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} onOpenInOrdersFlow={(orderId) => {
            sessionStorage.setItem('selectedWorkOrderId', orderId);
            setPreviousWidget('dailyReport');
            setSelectedWidget('ordersFlow');
          }} />
        )}
        {/* Stålplate Måling full screen */}
        {selectedWidget === 'steelMeasure' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <SteelMeasureWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Timeline full screen */}
        {selectedWidget === 'timeline' && (
          <div className="widget-panel-fullscreen">
            {/* Full screen content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <TimelineWidget 
                token={authState.token} 
                currentUser={enrichedUser} 
                onClose={handleCloseModal}
                onOpenWorkOrderDetails={(orderId) => {
                  console.log('[App] Opening work order details from Timeline for ID:', orderId);
                  sessionStorage.setItem('selectedWorkOrderId', orderId);
                  setPreviousWidget('timeline');
                  setSelectedWidget('workOrdersList');
                }}
              />
            </div>
          </div>
        )}
        {/* SteelCertificates full screen */}
        {selectedWidget === 'steelCertificates' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a'
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <SteelCertificateWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* SteelInventory full screen */}
        {selectedWidget === 'steelInventory' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <SteelInventoryWidget token={authState.token} currentUser={enrichedUser} userTeams={userTeams} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Lagringsplass full screen */}
        {selectedWidget === 'storageLocations' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <StorageLocationWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* PDF Redaksjon full screen */}
        {selectedWidget === 'pdfRedact' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <PdfRedactWidget onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Ny Arbeidsordre full screen */}
        {selectedWidget === 'workOrders' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <WorkOrderWidget
              token={authState.token}
              currentUser={enrichedUser}
              onOrderCreated={(createdOrder) => {
                console.log('[App] Order created:', createdOrder.id);
                setSelectedWidget('ordersFlow');
              }}
              onClose={handleCloseModal}
            />
          </div>
        )}
        {/* Forespørsler full screen */}
        {selectedWidget === 'foresporsler' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#f3f4f6',
          }}>
            <ForesporslerWidget 
              token={authState.token}
              currentUser={enrichedUser}
              onClose={handleCloseModal}
              companyName={dashboardSettings.companyName}
              initialInquiryId={pendingInquiryId}
              onOpenBom={(inquiryId) => {
                setBomInquiryId(inquiryId);
                setPreviousWidget('foresporsler');
                setSelectedWidget('bom');
              }}
            />
          </div>
        )}
        {/* BOM / Priskalkulator full screen */}
        {selectedWidget === 'bom' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <BOMWidget token={authState.token} currentUser={enrichedUser} initialInquiryId={bomInquiryId} onClose={handleCloseModal} onOpenInquiry={() => {
              setPreviousWidget('bom');
              setSelectedWidget('foresporsler');
            }} />
          </div>
        )}
        {/* Mottatte Pakker full screen */}
        {selectedWidget === 'incomingDelivery' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <IncomingDeliveryWidget
              token={authState.token}
              currentUser={enrichedUser}
              onClose={handleCloseModal}
            />
          </div>
        )}
        {/* Feed full screen */}
        {selectedWidget === 'feed' && (
          <div className="widget-panel-fullscreen" style={{ backgroundColor: '#0f172a' }}>
            <FeedWidget 
              token={authState.token}
              currentUser={enrichedUser}
              onClose={handleCloseModal}
            />
          </div>
        )}
        {/* Email full screen */}
        {selectedWidget === 'email' && (
          <div className="widget-panel-fullscreen" style={{ backgroundColor: '#f9fafb' }}>
            <EmailWidget 
              token={authState.token}
              currentUser={enrichedUser}
              onClose={handleCloseModal}
            />
          </div>
        )}
        {selectedWidget === 'aiChat' && (
          <div className="widget-panel-fullscreen" style={{ backgroundColor: '#f3f4f6' }}>
            <AIChatWidget 
              token={authState.token}
              currentUser={enrichedUser}
              onClose={handleCloseModal}
            />
          </div>
        )}
        {/* TeamCapacity full screen */}
        {selectedWidget === 'teamCapacity' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <TeamCapacityWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
          </div>
        )}
        {/* CAD Viewer full screen */}
        {selectedWidget === 'cadViewer' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#1a1a2e',
          }}>
            <CadViewerWidget
              token={authState.token}
              onClose={handleCloseModal}
              initialFile={cadViewerFile}
            />
          </div>
        )}
        {/* Adam CAD Generator full screen */}
        {selectedWidget === 'adamCad' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0b1220',
          }}>
            <AdamCadWidget
              token={authState.token}
              onClose={handleCloseModal}
            />
          </div>
        )}
        {/* Deler Utforsker full screen */}
        {selectedWidget === 'partsExplorer' && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <PartsExplorerWidget
              token={authState.token}
              onClose={handleCloseModal}
              initialSearchTerm={sessionStorage.getItem('partsExplorerSearch')}
              onOpenWorkOrderDetails={(orderId) => {
                sessionStorage.setItem('selectedWorkOrderId', orderId);
                setPreviousWidget('partsExplorer');
                setSelectedWidget('workOrdersList');
              }}
              onOpenInOrdersFlow={(orderId) => {
                sessionStorage.setItem('selectedWorkOrderId', orderId);
                setPreviousWidget('partsExplorer');
                setSelectedWidget('ordersFlow');
              }}
            />
          </div>
        )}
        {/* Machine Monitor full screen */}
        {selectedWidget === 'machineMonitor' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <MachineMonitorWidget
                token={authState.token}
                onClose={handleCloseModal}
                onOpenMachines={() => {
                  setPreviousWidget('machineMonitor');
                  setSelectedWidget('machines');
                }}
              />
            </div>
          </div>
        )}
        {/* Smart Scheduler full screen */}
        {selectedWidget === 'smartScheduler' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <SmartSchedulerWidget
                token={authState.token}
                currentUser={enrichedUser}
                onClose={handleCloseModal}
              />
            </div>
          </div>
        )}
        {/* AlarmLog full screen */}
        {selectedWidget === 'alarmLog' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <AlarmLogWidget token={authState.token} isAdmin={enrichedUser?.isAdmin} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* CNC Program Transfer - admin only, full screen */}
        {selectedWidget === 'cncProgramTransfer' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            background: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <CncProgramTransferWidget token={authState.token} currentUser={enrichedUser} userTeams={userTeams} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Forbruksskap full screen */}
        {selectedWidget === 'forbruksskap' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <ForbruksskapWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Stålinnkjøp full screen */}
        {selectedWidget === 'steelPurchases' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <SteelPurchaseWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Verktøyinventar full screen */}
        {selectedWidget === 'machiningTools' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <MachiningToolsWidget token={authState.token} currentUser={enrichedUser} onClose={handleCloseModal} />
            </div>
          </div>
        )}
        {/* Avvik widget */}
        {selectedWidget === 'avvik' && authState.isAuthenticated && (
          <div className="widget-panel-fullscreen" style={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: '#0f172a',
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <PartIssuesWidget token={authState.token} onClose={handleCloseModal} onOpenInOrdersFlow={(workOrderId, partId) => {
                sessionStorage.setItem('selectedWorkOrderId', workOrderId);
                if (partId) sessionStorage.setItem('selectedPartId', String(partId));
                setPreviousWidget('avvik');
                setSelectedWidget('ordersFlow');
              }} />
            </div>
          </div>
        )}
        </Suspense>
        {selectedWidget && selectedWidget !== 'ordersFlow' && selectedWidget !== 'productionPlan' && selectedWidget !== 'steelCertificates' && selectedWidget !== 'timeline' && selectedWidget !== 'kpiDashboard' && selectedWidget !== 'salesProgress' && selectedWidget !== 'deliveryDisplay' && selectedWidget !== 'feed' && selectedWidget !== 'aiChat' && selectedWidget !== 'foresporsler' && selectedWidget !== 'workOrders' && selectedWidget !== 'teamCapacity' && selectedWidget !== 'steelInventory' && selectedWidget !== 'email' && selectedWidget !== 'alleAnsatte' && selectedWidget !== 'cadViewer' && selectedWidget !== 'adamCad' && selectedWidget !== 'machineMonitor' && selectedWidget !== 'smartScheduler' && selectedWidget !== 'adminPanel' && selectedWidget !== 'jobQueue' && selectedWidget !== 'workOrdersList' && selectedWidget !== 'alarmLog' && selectedWidget !== 'forbruksskap' && selectedWidget !== 'bom' && selectedWidget !== 'partsExplorer' && selectedWidget !== 'incomingDelivery' && selectedWidget !== 'unimicroOrders' && selectedWidget !== 'teamMembers' && selectedWidget !== 'operatorWork' && selectedWidget !== 'storageLocations' && selectedWidget !== 'pdfRedact' && selectedWidget !== 'deliveryTracking' && selectedWidget !== 'dailyReport' && selectedWidget !== 'steelPurchases' && selectedWidget !== 'machiningTools' && selectedWidget !== 'steelMeasure' && selectedWidget !== 'avvik' && selectedWidget !== 'cncProgramTransfer' && (
          <WidgetModal
            title={(() => {
              switch (selectedWidget) {
                case 'configuration':
                  return 'Konfigurasjon';
                case 'workOrders':
                  return 'Nytt Arbeidsordre';
                case 'workOrdersList':
                  return 'Arbeidsordre';
                case 'ordersFlow':
                  return 'Ordreflyt';
                case 'productionPlan':
                  return 'Produksjonsplan';
                case 'jobQueue':
                  return 'Jobbkø';
                case 'roles':
                  return 'Roller';
                case 'employeeCalendar':
                  return 'Ansattplanlegger';
                case 'anstattkalendar':
                  return 'Anstattkalendar';
                case 'machines':
                  return 'Maskiner';
                case 'machineMonitor':
                  return 'Maskin Monitor';
                case 'teams':
                  return 'Team';
                case 'teamMembers':
                  return 'Team Medlemmer';
                case 'steelCertificates':
                  return 'Sertifikater';
                case 'steelInventory':
                  return 'Stållager';
                case 'steelPurchases':
                  return 'Stålinnkjøp';
                case 'machiningTools':
                  return 'Verktøyinventar';
                case 'steelMeasure':
                  return 'Stålplate Måling';
                case 'statistics':
                  return 'Statistikk';
                case 'storageLocations':
                  return 'Lagringsplass';
                case 'varslinger':
                  return 'Varslinger';
                case 'unimicro':
                  return 'Unimicro';
                case 'unimicrLookup':
                  return 'Unimicro Oppslag';
                case 'partsExplorer':
                  return 'Deler Utforsker';
                case 'timeline':
                  return 'Tidslinje';
                case 'kpiDashboard':
                  return 'KPI Dashboard';
                case 'salesProgress':
                  return 'Salgsprogresjon';
                case 'teamCapacity':
                  return 'Teamkapasitet';
                case 'smartScheduler':
                  return 'Smart Planlegging';
                case 'bom':
                  return 'Priskalkulator (BOM)';
                case 'weather':
                  return 'Været';
                case 'incomingDelivery':
                  return 'Mottatte Pakker';
                case 'email':
                  return 'E-post';
                case 'alleAnsatte':
                  return 'Felles Kalender';
                case 'brukere':
                  return 'Brukere';
                case 'phoneDirectory':
                  return 'Telefonliste';
                case 'absenceRequest':
                  return 'Fravær';
                case 'vektKalkulator':
                  return 'Vekt Kalkulator';
                case 'quickLinks':
                  return 'Hurtiglenker';
                case 'pdfRedact':
                  return 'PDF Redaksjon';
                case 'fileShare':
                  return 'Fildeling';
                default:
                  return 'Modulinnstillinger';
              }
            })()}
            onClose={handleCloseModal}
            fullViewport={selectedWidget === 'anstattkalendar' || selectedWidget === 'workOrders' || selectedWidget === 'workOrdersList' || selectedWidget === 'ordersFlow' || selectedWidget === 'teams' || selectedWidget === 'productionPlan' || selectedWidget === 'steelCertificates' || selectedWidget === 'machines' || selectedWidget === 'userManagement' || selectedWidget === 'roles' || selectedWidget === 'statistics' || selectedWidget === 'storageLocations' || selectedWidget === 'bom' || selectedWidget === 'steelInventory' || selectedWidget === 'unimicrLookup' || selectedWidget === 'alleAnsatte' || selectedWidget === 'brukere' || selectedWidget === 'auditLog' || selectedWidget === 'pdfRedact' || selectedWidget === 'steelPurchases' || selectedWidget === 'machiningTools'}
            hideHeader={selectedWidget === 'auditLog' || selectedWidget === 'machineMonitor' || selectedWidget === 'phoneDirectory' || selectedWidget === 'absenceRequest' || selectedWidget === 'fileShare' || selectedWidget === 'varslinger' || selectedWidget === 'vektKalkulator'}
            largeModal={selectedWidget === 'weather' || selectedWidget === 'vektKalkulator'}
            closeOnOverlayClick={selectedWidget !== 'workOrders'}
          >
            {renderWidgetModalContent()}
          </WidgetModal>
        )}
      </main>

      {/* Chat Message Banner Notifications - Stacked */}
      {chatBanners.map((banner, index) => (
        <div
          key={banner.id}
          onClick={() => {
            setChatBanners(prev => prev.filter(b => b.id !== banner.id));
            setShowChat(true);
            setUnreadChatCount(0);
          }}
          style={{
            position: 'fixed',
            top: `${20 + (index * 110)}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000 - index,
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '1.5rem 2rem',
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(59, 130, 246, 0.5), 0 0 0 4px rgba(59, 130, 246, 0.2)',
            cursor: 'pointer',
            minWidth: '400px',
            maxWidth: '600px',
            animation: 'slideDown 0.3s ease-out',
            border: '3px solid white'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              fontSize: '2rem',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              💬
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: '700',
                fontSize: '1.1rem',
                marginBottom: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {banner.isDM && <span style={{ fontSize: '0.9rem' }}>🔒</span>}
                Ny melding fra {banner.senderName}
              </div>
              <div style={{
                fontSize: '0.95rem',
                opacity: 0.95,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '500px'
              }}>
                {banner.message}
              </div>
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setChatBanners(prev => prev.filter(b => b.id !== banner.id));
              }}
              style={{
                fontSize: '1.5rem',
                opacity: 0.8,
                cursor: 'pointer',
                padding: '0.25rem',
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '0.8';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ✕
            </div>
          </div>
          <div style={{
            marginTop: '0.75rem',
            fontSize: '0.8rem',
            opacity: 0.8,
            textAlign: 'center'
          }}>
            Klikk for å åpne chat
          </div>
        </div>
      ))}

      {/* File Share Toast Notifications - Stacked */}
      {fileShareBanners.map((banner, index) => (
        <div
          key={banner.id}
          onClick={() => {
            setFileShareBanners(prev => prev.filter(b => b.id !== banner.id));
            setSelectedWidget('fileShare');
          }}
          style={{
            position: 'fixed',
            top: `${20 + (index * 100)}px`,
            right: '20px',
            zIndex: 10000 - index,
            backgroundColor: '#059669',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(5, 150, 105, 0.5), 0 0 0 4px rgba(5, 150, 105, 0.2)',
            cursor: 'pointer',
            minWidth: '320px',
            maxWidth: '450px',
            animation: 'slideDown 0.3s ease-out',
            border: '3px solid white'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontSize: '1.8rem' }}>📎</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.2rem' }}>
                Ny fil mottatt!
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.95, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {banner.fileName}
              </div>
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setFileShareBanners(prev => prev.filter(b => b.id !== banner.id));
              }}
              style={{
                fontSize: '1.3rem', opacity: 0.8, cursor: 'pointer', padding: '0.25rem',
                borderRadius: '8px', transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'; }}
              onMouseOut={(e) => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              ✕
            </div>
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', opacity: 0.8, textAlign: 'center' }}>
            Klikk for å åpne Fildeling
          </div>
        </div>
      ))}

      {/* Inquiry Assignment Banner Notification */}
      {inquiryBanner && (
        <div
          onClick={() => {
            setInquiryBanner(null);
            setSelectedWidget('foresporsler');
          }}
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            backgroundColor: '#10b981',
            color: 'white',
            padding: '1.5rem 2rem',
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(16, 185, 129, 0.5), 0 0 0 4px rgba(16, 185, 129, 0.2)',
            cursor: 'pointer',
            minWidth: '400px',
            maxWidth: '600px',
            animation: 'slideDown 0.3s ease-out',
            border: '3px solid white'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              fontSize: '2rem',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              📋
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: '700',
                fontSize: '1.1rem',
                marginBottom: '0.25rem'
              }}>
                Ny forespørsel tildelt
              </div>
              <div style={{
                fontSize: '0.95rem',
                opacity: 0.95,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '500px'
              }}>
                {inquiryBanner.assignedBy} har tildelt deg forespørsel fra {inquiryBanner.companyName}
              </div>
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setInquiryBanner(null);
              }}
              style={{
                fontSize: '1.5rem',
                opacity: 0.8,
                cursor: 'pointer',
                padding: '0.25rem',
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '0.8';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ✕
            </div>
          </div>
          <div style={{
            marginTop: '0.75rem',
            fontSize: '0.8rem',
            opacity: 0.8,
            textAlign: 'center'
          }}>
            Klikk for å åpne Forespørsler
          </div>
        </div>
      )}

      {/* Update Available Toast */}
      {showUpdateToast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          background: 'linear-gradient(135deg, #3b82f6, #3b82f6)',
          color: 'white',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          boxShadow: '0 8px 32px rgba(37, 99, 235, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          maxWidth: '380px',
          animation: 'slideUp 0.3s ease-out',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.25rem' }}>
              Ny versjon tilgjengelig
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              Serveren har blitt oppdatert. Last siden på nytt for å få de siste endringene.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'white',
                color: '#3b82f6',
                border: 'none',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Last på nytt
            </button>
            <button
              onClick={() => setShowUpdateToast(false)}
              style={{
                background: 'transparent',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                padding: '0.5rem 0.75rem',
                fontWeight: 500,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Senere
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateX(-50%) translateY(-100px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.2);
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default App;
